import React from "react";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";

import { NoResults, useAnalytics } from "@examspark/common-app";
import {
  Button,
  Modal,
  RouterLink,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import { IconSearchLine } from "@examspark/common-ui/dist/components/icons";
import SearchField from "@examspark/common-ui/dist/components/inputs/SearchField";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";

import BrowseList, { EmptyBox } from "../browse/BrowseList";
import { useSearch } from "../browse/useSearch";

const StyledBrowseList = styled.div<{ isSearching: boolean }>`
  transition: filter 0.25s ease-in-out;
  ${({ isSearching }) =>
    isSearching &&
    `
    // may turn this on later, with feedback
    filter: grayscale(1) opacity(0.5) blur(2px);
  `}
`;

type QuickSearchModalProps = {
  isSearchModalOpen: boolean;
  setIsSearchModalOpen: (isOpen: boolean) => void;
};
const QuickSearchModal = ({
  isSearchModalOpen,
  setIsSearchModalOpen,
}: QuickSearchModalProps) => {
  const isSmall = useMediaQuery({ maxWidth: 600 });
  const [searchQuery, setSearchQuery] = React.useState("");
  const analytics = useAnalytics();
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);

  const { data, isFetching } = useSearch({
    enabled: isSearchModalOpen,
    queryKey: [debouncedSearchQuery],
    filter: {
      grade: [],
      type: [
        "COURSE",
        "DOCUMENT",
        "ITEM",
        "PERFORMANCE_TASK",
        "RUBRIC",
        "UNIT",
      ],
      query: debouncedSearchQuery,
    },
  });

  return (
    <Modal
      title="Search"
      isOpen={isSearchModalOpen}
      onAfterOpen={() => {
        analytics.trackEvent("Open Quick Search");
      }}
      onRequestClose={() => setIsSearchModalOpen(false)}
      size={isSmall ? "mobile" : "large"}
      footer={
        <Modal.BasicFooter>
          <Toolbar>
            <span>
              For advanced search,{" "}
              <RouterLink
                to="/browse"
                onClick={() => setIsSearchModalOpen(false)}
              >
                Browse All Content
              </RouterLink>
            </span>
          </Toolbar>
        </Modal.BasicFooter>
      }
    >
      <Modal.PaddedBody>
        <VSpace>
          <Toolbar>
            <SearchField
              width="100%"
              placeholder="Search..."
              value={searchQuery}
              onInput={(e) => {
                setSearchQuery(e.currentTarget.value);
              }}
            />
            <Button
              title="Clear Search"
              onClick={() => {
                setSearchQuery("");
              }}
            >
              Clear
            </Button>
          </Toolbar>
          {searchQuery ? (
            <>
              {/* Announce the search results to the screen reader */}
              <Toolbar.Right>
                <label aria-live="assertive">
                  {isFetching
                    ? "Searching..."
                    : `
                  ${data?.total ?? 0} Results found
                `}
                </label>
              </Toolbar.Right>
              <StyledBrowseList
                isSearching={searchQuery !== debouncedSearchQuery}
              >
                <BrowseList
                  data={data?.items ?? []}
                  query={debouncedSearchQuery}
                  onResultNavigate={() => setIsSearchModalOpen(false)}
                />
              </StyledBrowseList>
            </>
          ) : (
            <EmptyBox>
              <NoResults icon={<IconSearchLine style={{ width: "3rem" }} />}>
                Start typing to search all content.
              </NoResults>
            </EmptyBox>
          )}
        </VSpace>
      </Modal.PaddedBody>
    </Modal>
  );
};

export default QuickSearchModal;
